import { useSearchParams } from "react-router-dom";
import { STEP_PARAM } from "../constants";

function scrollToPageTop() {
  window.requestAnimationFrame(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  });
}

export function useWizardSteps({
  stepCount,
  canLeaveStep,
}: {
  stepCount: number;
  canLeaveStep: (step: number) => boolean | Promise<boolean>;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawStep = searchParams.get(STEP_PARAM);
  const parsedStep = rawStep === null ? 0 : Number.parseInt(rawStep, 10);
  const activeStep = Number.isFinite(parsedStep)
    ? Math.max(0, Math.min(parsedStep, stepCount - 1))
    : 0;

  const goTo = (nextStep: number) => {
    const bounded = Math.max(0, Math.min(nextStep, stepCount - 1));
    if (bounded === activeStep && rawStep === String(bounded)) return;
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set(STEP_PARAM, String(bounded));
        return next;
      },
      { replace: true },
    );
    if (bounded !== activeStep) scrollToPageTop();
  };

  const goNext = async () => {
    if (!(await canLeaveStep(activeStep))) return;
    goTo(activeStep + 1);
  };

  const goBack = () => goTo(activeStep - 1);

  return { activeStep, stepInUrl: rawStep !== null, goTo, goNext, goBack };
}
